// utils/log.js
//
// The ONE place the portal writes to the browser console.
//
// WHY: 502 direct console.* calls across 92 files (2 Sep 2026), none gated on
// environment. In production every one of them printed to any user who opened
// DevTools — including AuthContext dumping whole request bodies, which carry
// the session Key. The owner: "there is way too much information being shared
// throughout the project."
//
// Production: debug/log/warn are silent. error prints ONE scrubbed line — the
// message only, never an object — and hands the details to errorReporter, which
// is the sanctioned path for failures. Development: everything passes through
// so nothing is lost while working.
//
// `no-console` is an ESLint error everywhere except this file and
// errorReporter, so a raw console call cannot creep back in.

import { reportError, scrub } from '@/utils/errorReporter';

const isProd = process.env.NODE_ENV === 'production';

/** Render a console argument as safe text: strings scrubbed, objects reduced to their type. */
function safeText(arg) {
    if (arg instanceof Error) return scrub(arg.message);
    if (typeof arg === 'string') return scrub(arg);
    if (arg === null || arg === undefined) return String(arg);
    if (typeof arg === 'object') return `[${Array.isArray(arg) ? 'array' : 'object'}]`;
    return scrub(String(arg));
}

function passthrough(method) {
    return (...args) => { console[method](...args); };
}

const silent = () => {};

export const logger = {
    debug: isProd ? silent : passthrough('log'),
    log:   isProd ? silent : passthrough('log'),
    warn:  isProd ? silent : passthrough('warn'),
    error: isProd
        ? (...args) => {
            const line = args.map(safeText).join(' ');
            console.error(line);
            const err = args.find(a => a instanceof Error);
            reportError({ category: 'Client', rawMessage: line, error: err });
        }
        : passthrough('error'),
};

export default logger;
